























































































































































import axios from 'axios';
import Component from 'vue-class-component';

import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiDatePicker2 from '@/components/ui/UiDatePicker2.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiSelect2 from '@/components/ui/UiSelect2.vue';
import UiTextEdit from '@/components/ui/UiTextEdit.vue';
import { MUT_SNACKBAR } from '@/store';
import { getInclusiveEndSec, getInclusiveStartSec, getLocalDateString } from '@/utils/dateUtils';
import { requestParentToNavigate } from '@/utils/iframeMessageRequester';
import {
  describePricingRouteSaveError,
  isDisabledChanged,
  LoadedPricingRoute,
  loadedPricingRoute,
  pricingRouteErrorReason,
  walletIdsUpdate,
  withRouteSaveGuards,
} from '@/utils/pricingRouteSave';

@Component({
  components: {
    UiPageHeader,
    UiButton,
    UiSelect2,
    UiTextEdit,
    UiCheckbox,
    UiDatePicker2,
  },
})
export default class PricingContextRoute extends BaseVue {
  public baseUrl = process.env.VUE_APP_API_URL;

  public isNew?: boolean = false;
  public pricingRouteId?: string;
  public isLoading = false;
  public isSaving = false;
  public pricingContexts: any[] = [];
  //  the route as loaded (version, wallets, disabled), for the save guards (CIB-4112); null until it loads
  public loadedRoute: LoadedPricingRoute | null = null;

  public directionOptions = [
    { label: 'Inflow', id: 'inflow' },
    { label: 'Outflow', id: 'outflow' },
    { label: 'Bi-directional', id: 'bi-directional' },
  ];

  public pricingRouteForm = {
    routeToContextId: null,
    walletIds: [] as string[],
    priority: 0,
    direction: null,
    isDisabled: false,
    metadata: [{ key: '', value: '', operator: '=' }] as { key: string; value: string; operator: string }[],
    inclusiveStartTimestampSEC: undefined as string | undefined,
    inclusiveEndTimestampSEC: undefined as string | undefined,
  };

  async mounted() {
    const pricingRouteId = this.$route.params.id;
    if (pricingRouteId === 'new') {
      this.isNew = true;
    } else {
      this.pricingRouteId = pricingRouteId;
    }

    await this.loadPricingContexts();

    if (!this.isNew && this.pricingRouteId) {
      await this.loadExistingPricingRoute(this.pricingRouteId);
    }
  }

  private async loadPricingContexts() {
    try {
      this.isLoading = true;
      const resp = await axios.get(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts`, {
        withCredentials: true,
      });

      this.pricingContexts = resp.data
        .map((context: any) => ({
          id: context.id,
          name: context.description || context.id,
        }))
        .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
    } catch (error) {
      console.error('Error loading pricing contexts:', error);
      this.$store.commit(MUT_SNACKBAR, {
        message: 'Error loading pricing contexts',
        color: 'error',
      });
    } finally {
      this.isLoading = false;
    }
  }

  private async loadExistingPricingRoute(routeId: string) {
    try {
      this.isLoading = true;
      const resp = await axios.get(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-context-routes`, {
        withCredentials: true,
      });

      const pricingRoutes = resp.data;
      const existingRoute = pricingRoutes.find((route: any) => route.id === routeId);

      if (existingRoute) {
        this.populateFormWithExistingData(existingRoute);
      } else {
        console.error(`Pricing route with ID ${routeId} not found`);
        this.$store.commit(MUT_SNACKBAR, {
          message: `Pricing route with ID ${routeId} not found`,
          color: 'error',
        });
      }
    } catch (error) {
      console.error('Error loading pricing route:', error);
      this.$store.commit(MUT_SNACKBAR, {
        message: 'Error loading pricing route',
        color: 'error',
      });
    } finally {
      this.isLoading = false;
    }
  }

  private convertSecondsToDateString(seconds: number | undefined): string | undefined {
    if (!seconds) return undefined;
    return getLocalDateString(this.$store.state.currentOrg.timezone, seconds);
  }

  private populateFormWithExistingData(route: any) {
    this.loadedRoute = loadedPricingRoute(route);
    this.pricingRouteForm = {
      routeToContextId: route.routeToContextId || null,
      walletIds: [...this.loadedRoute.walletIds],
      priority: route.priority || 0,
      direction: route.direction || null,
      isDisabled: route.isDisabled || false,
      metadata: route.metadata || [],
      inclusiveStartTimestampSEC: this.convertSecondsToDateString(route.inclusiveStartTimestampSEC) || undefined,
      inclusiveEndTimestampSEC: this.convertSecondsToDateString(route.inclusiveEndTimestampSEC) || undefined,
    };
  }

  get wallets() {
    return this.$store.state.wallets.wallets ?? [];
  }

  public addMetadataItem() {
    console.log('Adding metadata item');
    this.pricingRouteForm.metadata.push({
      key: '',
      value: '',
      operator: '=',
    });
  }

  public removeMetadataItem(index: number) {
    this.pricingRouteForm.metadata.splice(index, 1);
  }

  public async onSavePricingRoute() {
    try {
      this.isSaving = true;
      const timezone = this.$store.state.currentOrg.timezone;

      if (this.isNew) {
        // Create new pricing route
        const payload = {
          routeToContextId: this.pricingRouteForm.routeToContextId,
          walletIds: this.pricingRouteForm.walletIds.length > 0 ? this.pricingRouteForm.walletIds : undefined,
          priority: this.pricingRouteForm.priority,
          direction: this.pricingRouteForm.direction,
          isDisabled: this.pricingRouteForm.isDisabled,
          orgId: this.$store.state.currentOrg.id,
          metadata: this.pricingRouteForm.metadata,
          inclusiveStartTimestampSEC: this.pricingRouteForm.inclusiveStartTimestampSEC
            ? getInclusiveStartSec(timezone, this.pricingRouteForm.inclusiveStartTimestampSEC)
            : undefined,
          inclusiveEndTimestampSEC: this.pricingRouteForm.inclusiveEndTimestampSEC
            ? getInclusiveEndSec(timezone, this.pricingRouteForm.inclusiveEndTimestampSEC)
            : undefined,
        };

        await axios.post(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-context-routes`, payload, {
          withCredentials: true,
        });
      } else {
        const loaded = this.loadedRoute;
        if (loaded === null) {
          //  the route never loaded, so the form holds the blank defaults: saving would overwrite the
          //  stored route with them (no metadata, no wallets, no direction)
          this.$store.commit(MUT_SNACKBAR, {
            color: 'error',
            message: 'This pricing route did not load, so it cannot be saved. Reload the page and try again.',
          });
          return;
        }

        const routeUrl = `${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-context-routes/${this.pricingRouteId}`;

        const putPayload = withRouteSaveGuards(
          {
            routeToContextId: this.pricingRouteForm.routeToContextId,
            walletIds: walletIdsUpdate(loaded.walletIds, this.pricingRouteForm.walletIds),
            priority: this.pricingRouteForm.priority,
            direction: this.pricingRouteForm.direction,
            metadata: this.pricingRouteForm.metadata,
            inclusiveStartTimestampSEC: this.pricingRouteForm.inclusiveStartTimestampSEC
              ? getInclusiveStartSec(timezone, this.pricingRouteForm.inclusiveStartTimestampSEC)
              : undefined,
            inclusiveEndTimestampSEC: this.pricingRouteForm.inclusiveEndTimestampSEC
              ? getInclusiveEndSec(timezone, this.pricingRouteForm.inclusiveEndTimestampSEC)
              : undefined,
          },
          loaded
        );

        await axios.put(routeUrl, putPayload, { withCredentials: true });

        if (isDisabledChanged(loaded, this.pricingRouteForm.isDisabled)) {
          try {
            await axios.patch(routeUrl, { isDisabled: this.pricingRouteForm.isDisabled }, { withCredentials: true });
          } catch (e) {
            //  the PUT is already saved and moved the route's version; reload so the form and the version
            //  match what is stored, otherwise every retry would be refused as a stale save
            this.$store.commit(MUT_SNACKBAR, {
              color: 'error',
              message: `Your changes were saved, but ${
                this.pricingRouteForm.isDisabled ? 'disabling' : 'enabling'
              } the route failed: ${pricingRouteErrorReason(e)}. The form now shows what is saved.`,
            });
            await this.loadExistingPricingRoute(this.pricingRouteId as string);
            return;
          }
        }
      }

      this.$store.commit(MUT_SNACKBAR, {
        message: 'Pricing route saved successfully',
        color: 'success',
      });
      this.requestParentToNavigate('/pricing/context');
    } catch (e) {
      this.$store.commit(MUT_SNACKBAR, {
        color: 'error',
        message: describePricingRouteSaveError(e),
      });
    } finally {
      this.isSaving = false;
    }
  }

  public requestParentToNavigate(path: string) {
    requestParentToNavigate(path);
  }
}
